import { SubscriptionClient } from 'subscriptions-transport-ws';
import { WebSocketLink } from '@apollo/client/link/ws/index.js';
import { HttpLink } from '@apollo/client/link/http/index.js';
import { onError } from '@apollo/client/link/error/index.js';
import {
  ApolloClient,
  split,
  InMemoryCache,
  ApolloLink,
  makeVar,
} from '@apollo/client';
import { WS_API_HOST, API_HOST } from './app-constants';
import { getMainDefinition } from '@apollo/client/utilities';

// Authenticated user
export const userVar = makeVar<any>({});
// Websocket health check
export const wsIsActiveVar = makeVar(false);
// Server/network health check
export const serverIsAliveVar = makeVar(true);

const wsClient = new SubscriptionClient(WS_API_HOST, {
  reconnect: true,
  connectionParams: () => ({ token: userVar()?.token }),
  connectionCallback: () => {
    wsIsActiveVar(true);
    serverIsAliveVar(true);
  },
  lazy: true,
});
wsClient.onDisconnected(() => serverIsAliveVar(false));

const wsLink = new WebSocketLink(wsClient);

const httpLink = new HttpLink({
   uri: API_HOST,
   credentials: 'include',
 });

const errorLink = onError(
  ({ graphQLErrors, networkError, forward, operation }) => {
    if (networkError) {
      console.error('[APOLLO CLIENT]: Network error', '\n', networkError);
      serverIsAliveVar(false);
    } else {
      serverIsAliveVar(true);
    }
    if (graphQLErrors) {
      console.error(
        '[APOLLO CLIENT]: GraphQL errors', '\n', 'Errors list:', graphQLErrors, '\n', 'Operation:', operation,
      );

      // Reload page if auth token expired
      if ((graphQLErrors?.[0] as any)?.code === 401
       && operation.operationName !== 'getAuth') window.location.reload();
      // return redirect("/login");
    }
    return forward(operation);
  },
);

const link = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === 'OperationDefinition'
        && (definition as any).operation === 'subscription'
    );
  },
  wsLink,
  httpLink
);

export const gqlClient = new ApolloClient({
  cache: new InMemoryCache({
    addTypename: false,
  }),
  link: ApolloLink.from([ errorLink, link ]),
  defaultOptions: {
    watchQuery: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'none',
    },
    query: {
      fetchPolicy: 'no-cache',
      errorPolicy: 'none',
    },
    mutate: {
      errorPolicy: 'all',
    },
  },
});

export const processResponse = <T extends Record<string, any>>(
  obj: any,
  config: {
    defaults?: Partial<T>;
    typeCasts?: { [K in keyof T]?: (value: any) => T[K] };
    required?: boolean;
  } = {}
): T => {
  if (obj === null || obj === undefined) {
    if (config.required) {
      return createDefaultObject<T>(config.defaults);
    }
    return undefined as unknown as T;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => processResponse(item, config)) as unknown as T;
  }

  if (typeof obj === 'object') {
    const result: any = {};

    // Обробка всіх полів з оригінального об'єкта
    for (const key in obj) {
      const value = obj[key];

      if (value === null) {
        result[key] = config.defaults?.[key] ?? undefined;
      } else if (config.typeCasts && key in config.typeCasts && config.typeCasts[key]) {
        result[key] = config.typeCasts[key]!(value); // Використовуємо non-null assertion
      } else {
        result[key] = processResponse(value, config);
      }
    }

    // Додавання дефолтних значень для обов'язкових полів, якщо required: true
    if (config.required && config.defaults) {
      for (const key in config.defaults) {
        if (result[key] === undefined) {
          result[key] = config.defaults[key];
        }
      }
    }

    return result as T;
  }

  return obj as T;
};

const createDefaultObject = <T>(defaults?: Partial<T>): T => ({ ...defaults } as T);
